import { ExcelController } from "@controllers/Excel.controller";
import { Association, Opportunity } from "@entities";
import { ChildFriendlyRounded } from "@mui/icons-material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { CircularProgress, Dialog, Stack } from "@mui/material";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { OrganizationRoles } from "@shared/Roles";
import { type FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GeneralAlert } from "../../../components/alert/GeneralAlert";
import AidRelatedCard from "../../../components/popups/aid-related-card/AidRelatedCard";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useRequest } from "../../../contexts/RequestProvider";
import { useCloseQuestion } from "../../../generic-functions/close-question.function";
import { downloadExcelFile } from "../../../helpers/excel/export";
import OrganizationRequest from "../../organization-request/OrganizationRequest";
import { TableHeaderTitles } from "../table-header/TableHeader";
import { AidRelatedColumns, associationRequestedColumns } from "./columns";
import filters from "./filters";

const AidRelatedTable: FC = () => {
  const { HyperTable } = useCreateHyperTable(Association);
  const { HyperTable: SubTable } = useCreateHyperTable(Opportunity);
  const { currOrganization } = useOrganization();
  const [selectedGroups, setSelectedGroups] = useState<Association[]>([]);
  const [aidRelated, setAidRelated] = useState<Association>();
  const [isAddAidRelated, setIsAddAidRelated] = useState<boolean>(false);
  const [request, setRequest] = useState<Opportunity | null>(null);
  const navigate = useNavigate();
  const now = new Date();
  const { resetRequest, isTouched } = useRequest();
  const closeQuestion = useCloseQuestion();

  useEffect(() => {
    if (!request) {
      HyperTable.context?.current?.refetch("remove");
    }
  }, [request]);

  const buttons: ActionButton<Association>[] = useMemo(() => {
    return [
      {
        icon: <FileUploadIcon />,
        onClick: async ({ whereToUpdate }) => {
          const excelResult =
            await ExcelController.exportAidRelatedUsersToExcel(whereToUpdate);
          if (excelResult) {
            downloadExcelFile(excelResult);
          }

          return {
            refetch: false,
            resetSelection: true,
          };
        },
        title: "ייצוא לאקסל",
      },
    ];
  }, [selectedGroups]);

  if (!currOrganization)
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );

  return (
    <HyperTable
      tableId="association-requested-table"
      components={{
        addRowButton: {
          onClick: () => setIsAddAidRelated(true),
          content: "הוספת מבקש סיוע",
        },
      }}
      findConfig={{
        enabled: !!currOrganization?.id,
        limit: 15,
        initialOrderBy: { requestedOpportunitiesCount: "desc" },
        where: Association.hyperFilter({
          $and: [
            {
              roles: {
                $some: {
                  $contains: OrganizationRoles.Requesting,
                },
              },
            },
          ],
        }),
        include: {
          requestedOpportunities: {
            where: {
              isDeleted: false,
            },
            orderBy: { createdAt: "desc" },
            limit: 25,
            include: {
              teams: false,
              manager: true,
              progressStatus: false,
              partnerOrganization: true,
            },
          },
          user: true,
        },
      }}
      onRowClick={(_, row) => setAidRelated(row.original)}
      title={<TableHeaderTitles titles={[{ content: "בקשות ומבקשי סיוע" }]} />}
      tableSettings={{
        getSubRows: (row) => row.requestedOpportunities as any[],
      }}
      renderSubRows={(row) => {
        return (
          <SubTable
            components={{
              paginate: null,
              rowCounter: null,
              loading: null,
            }}
            tableId="aid-related-opportunities-table"
            findConfig={{ enabled: false }}
            queryOptions={{ enabled: false }}
            tableSettings={{
              data: row.requestedOpportunities,
            }}
            onRowClick={(_, row) => setRequest(row.original)}
          >
            {associationRequestedColumns.map((column) => (
              <SubTable.Column key={column.field} {...column} />
            ))}
            <Dialog
              open={!!request}
              PaperProps={{
                sx: {
                  width: "40vw",
                  height: "90vh",
                  maxWidth: "90vw",
                  margin: 0,
                  overflow: "hidden",
                },
              }}
              onClose={() => {
                if (isTouched) {
                  closeQuestion({
                    onYes: () => {
                      setRequest(null);
                      resetRequest();
                    },
                  });
                } else {
                  setRequest(null);
                  resetRequest();
                }
              }}
            >
              <OrganizationRequest
                setIsAddEvent={() => setRequest(null)}
                eventData={request || undefined}
                fullPage={false}
              />
              <GeneralAlert />
            </Dialog>
          </SubTable>
        );
      }}
    >
      <HyperTable.Toolbar
        moreActions={[
          {
            label: "תצוגת בקשות",
            icon: <ChildFriendlyRounded fontSize="small" />,
            onClick: () => navigate("/user-requests"),
          },
        ]}
      >
        {filters.map((filter) =>
          isEntityFilter(filter) ? (
            <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
          ) : (
            <HyperTable.CustomFilter key={filter.id} {...filter} />
          ),
        )}
      </HyperTable.Toolbar>
      <HyperTable.ExpandColumn />
      {AidRelatedColumns.map((column) => (
        <HyperTable.Column key={column.field} {...column} />
      ))}
      {/* <HyperTable.SelectColumn onChange={(selectedRows) => setSelectedGroups(selectedRows)}></HyperTable.SelectColumn> */}
      <HyperTable.ActionsBar buttons={buttons} />
      <AidRelatedCard
        isAdd={isAddAidRelated}
        onSave={async (association: Association) => {
          // Refresh the table after adding new aid-related
          HyperTable.context?.current?.refetch("remove");
          setIsAddAidRelated(false);
        }}
        requestedOpportunities={aidRelated?.requestedOpportunities || []}
        defaultValues={aidRelated}
        onClose={() => {
          setAidRelated(undefined);
          setIsAddAidRelated(false);
        }}
      />
    </HyperTable>
  );
};

export default AidRelatedTable;
